// @ts-check
import { KEY_CODE } from '../constants/constants.js';
import {
  getZIPCodeInitialValue, detectZIPCodeValue, saveZIPCodeValue, isZIPCodeValid
} from './zip-code-service.js';

/** @typedef {import('./get-settings').CommerceConnectorSettings} CommerceConnectorSettings */
/** @typedef {import('./zip-code-service').ZIPCodeParams} ZIPCodeParams */

/** Manages ZIP code value */
export default class ZIPCodeInput {
  /** @type {ZIPCodeParams} Commerce Connector settings */
  params;

  /** @type {HTMLInputElement} Associated input element */
  input;

  isDirty = false;

  /** @type {string | null} */
  detectedValue;

  /**
   * @param {HTMLInputElement} input
   * @param {ZIPCodeParams} zipParams
   * @param {Object} callbacks
   * @param {({ isValid: boolean }) => void} [callbacks.onChange]
   */
  constructor(input, zipParams, callbacks = {}) {
    this.input = input;
    this.params = zipParams;

    const { onChange } = callbacks;

    this.input.maxLength = this.params.length;

    this.input.addEventListener('input', () => {
      this.isDirty = true;

      if (onChange) {
        onChange({ isValid: this.isValid() });
      }
    });

    // Clear value on 'Escape'
    this.input.addEventListener('keyup', (e) => {
      if (e.keyCode === KEY_CODE.escape) {
        this.input.value = '';
        // Prevent modal closing
        e.stopPropagation();
      }
    });

    this.input.value = getZIPCodeInitialValue(this.params);

    // Save detected ZIP code to check later whether detected code is in use
    if (this.params.detect) {
      this.detectedValue = detectZIPCodeValue();
    }
  }

  /**
   * Return current value of ZIP code
   * @returns {string}
   */
  getValue() {
    return this.input.value.trim();
  }

  /**
   * Validate ZIP code field
   * @returns {boolean} If zip code valid
   */
  isValid() {
    return isZIPCodeValid(this.getValue(), this.params);
  }

  isEmpty() {
    return !this.getValue();
  }

  /** Whether ZIP code value is detected from geolocation */
  isDetected() {
    return this.getValue() === this.detectedValue && !this.isDirty;
  }

  /** Save ZIP code value to localStorage */
  save() {
    const value = this.getValue();

    if (value && this.isDirty) {
      saveZIPCodeValue(value, this.params);
    }
  }

  /**
   * Get an object that replaces ZIPCodeInput when ZIP code isn't used
   * @returns {ZIPCodeInput}
   */
  static getNullObject() {
    return {
      params: null,
      input: null,
      isDirty: false,
      detectedValue: null,
      getValue() { return ''; },
      isValid() { return false; },
      isEmpty() { return true; },
      isDetected() { return false; },
      save() { }
    };
  }
}
